import React from "react";
import {
  AppEnvironment,
  getEnvironmentDisplayName,
  type FeatureFlag,
} from "src/lib/featureFlagsService";
import { Edit2, Save, Trash2, X } from "lucide-react";
import { FcAndroidOs } from "react-icons/fc";
import { FaApple, FaGlobe } from "react-icons/fa";

export interface FlagWithKey extends FeatureFlag {
  key: string;
}

const slab = {
  card: "rounded-[1.5rem] border-4 border-[#E5E5E5] bg-white shadow-[0_6px_0_#D0D0D0]",
  label: "text-xs font-extrabold uppercase tracking-wide text-[#777777]",
  input:
    "min-h-[40px] w-full rounded-lg border-2 border-[#E5E5E5] bg-[#FAFAFA] px-2.5 py-1.5 text-sm font-semibold text-[#3C3C3C] focus:border-[#6366F1] focus:outline-none focus:ring-4 focus:ring-[#6366F1]/25",
  chipIndigo:
    "rounded-md bg-[#EEF2FF] px-1.5 py-0.5 text-[11px] font-extrabold leading-tight text-[#4338CA]",
  chipPlatform:
    "inline-flex items-center gap-1 rounded-md bg-[#FFFBEB] px-1.5 py-0.5 text-[11px] font-bold leading-tight text-[#B45309]",
} as const;

function platformIcon(p: string) {
  switch (p.toLowerCase()) {
    case "ios":
      return <FaApple className="text-[#3C3C3C]" aria-hidden />;
    case "android":
      return <FcAndroidOs aria-hidden />;
    case "web":
      return <FaGlobe className="text-[#6366F1]" aria-hidden />;
    default:
      return null;
  }
}

export type FeatureFlagCardProps = {
  flag: FlagWithKey;
  editing: boolean;
  editForm: Partial<FeatureFlag>;
  canChangeSettings: boolean;
  busy: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  setEditForm: React.Dispatch<React.SetStateAction<Partial<FeatureFlag>>>;
};

export function FeatureFlagCard({
  flag,
  editing,
  editForm,
  canChangeSettings,
  busy,
  onToggle,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  setEditForm,
}: FeatureFlagCardProps) {
  return (
    <article className={`overflow-hidden ${slab.card}`}>
      <div className="flex flex-col gap-2.5 p-3 sm:p-4">
        <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-3">
          <div className="min-w-0">
            <p className={slab.label}>Platforms</p>
            {editing && canChangeSettings ? (
              <div className="mt-1 flex flex-col gap-1">
                {["ios", "android"].map((platform) => (
                  <label
                    key={platform}
                    className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#3C3C3C]"
                  >
                    <input
                      type="checkbox"
                      checked={editForm.platforms?.includes(platform) ?? false}
                      onChange={(e) => {
                        const platforms = editForm.platforms || [];
                        setEditForm({
                          ...editForm,
                          platforms: e.target.checked
                            ? [...platforms, platform]
                            : platforms.filter((x) => x !== platform),
                        });
                      }}
                      className="h-4 w-4 rounded border-2 border-[#E5E5E5] text-[#6366F1] focus:ring-4 focus:ring-[#6366F1]/30"
                    />
                    <span className="capitalize">{platform}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {flag.platforms && flag.platforms.length > 0 ? (
                  flag.platforms.map((p) => (
                    <span key={p} className={slab.chipPlatform} title={p}>
                      {platformIcon(p)}
                      <span className="capitalize">{p}</span>
                    </span>
                  ))
                ) : (
                  <span className="text-xs font-semibold text-[#777777]">
                    All platforms
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className={slab.label}>Environments</p>
            {editing && canChangeSettings ? (
              <div className="mt-1 max-h-28 space-y-0.5 overflow-y-auto rounded-lg border-2 border-[#F4F4F5] p-1.5">
                {Object.values(AppEnvironment).map((env) => (
                  <label
                    key={env}
                    className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#3C3C3C]"
                  >
                    <input
                      type="checkbox"
                      checked={editForm.environment?.includes(env) ?? false}
                      onChange={(e) => {
                        const environments = editForm.environment || [];
                        setEditForm({
                          ...editForm,
                          environment: e.target.checked
                            ? [...environments, env]
                            : environments.filter((x) => x !== env),
                        });
                      }}
                      className="h-4 w-4 rounded border-2 border-[#E5E5E5] text-[#6366F1] focus:ring-4 focus:ring-[#6366F1]/30"
                    />
                    <span>{getEnvironmentDisplayName(env)}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1">
                {flag.environment && flag.environment.length > 0 ? (
                  flag.environment.map((env) => (
                    <span key={env} className={slab.chipIndigo}>
                      {getEnvironmentDisplayName(env as AppEnvironment)}
                    </span>
                  ))
                ) : (
                  <span className="text-xs font-semibold text-[#777777]">
                    None (disabled)
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className={slab.label}>Min build</p>
            {editing && canChangeSettings ? (
              <input
                type="number"
                value={editForm.min_build ?? ""}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    min_build: e.target.value
                      ? parseInt(e.target.value, 10)
                      : undefined,
                  })
                }
                placeholder="Optional"
                className={`mt-1 ${slab.input}`}
              />
            ) : (
              <p className="mt-1 font-mono text-xs font-bold text-[#3C3C3C] sm:text-sm">
                {flag.min_build ?? "—"}
              </p>
            )}
          </div>
        </div>

        {editing && canChangeSettings ? (
          <div>
            <p className={slab.label}>Description</p>
            <input
              type="text"
              value={editForm.description ?? ""}
              onChange={(e) =>
                setEditForm({ ...editForm, description: e.target.value })
              }
              placeholder="What this flag controls"
              className={`mt-1 ${slab.input}`}
            />
          </div>
        ) : null}

        {canChangeSettings && (
          <div className="flex flex-wrap gap-2 border-t border-[#F4F4F5] pt-2.5">
            {editing ? (
              <>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={busy}
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#14B8A6] px-4 text-xs font-extrabold uppercase tracking-wide text-white shadow-[0_4px_0_#0F766E] transition active:translate-y-1 active:shadow-[0_2px_0_#0F766E] disabled:opacity-50 sm:flex-none sm:text-sm"
                >
                  <Save className="h-4 w-4" aria-hidden />
                  Save
                </button>
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border-2 border-[#E5E5E5] bg-white px-4 text-xs font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0] transition active:translate-y-0.5 sm:text-sm"
                >
                  <X className="h-4 w-4" aria-hidden />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg border-2 border-[#E5E5E5] bg-white px-3 text-xs font-extrabold text-[#4338CA] shadow-[0_3px_0_#C7D2FE] transition hover:bg-[#FAFAFA] active:translate-y-0.5 sm:flex-none sm:text-sm"
                >
                  <Edit2 className="h-4 w-4" aria-hidden />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={busy}
                  className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border-2 border-[#FECACA] bg-[#FEF2F2] px-3 text-xs font-extrabold text-[#EF4444] shadow-[0_3px_0_#FECACA] transition active:translate-y-0.5 disabled:opacity-50 sm:text-sm"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                  Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
